import { useState } from "react";
import axios from "axios";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect
  const token = localStorage.getItem("blancspace_admin_token");
  if (token) return <Navigate to="/admin" replace />;

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("blancspace_admin_token", data.access_token);
      localStorage.setItem("blancspace_admin_email", data.email);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Login failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-black flex items-center justify-center px-6 relative overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="hero-gradient-orb-1" />
      <div className="hero-gradient-orb-2" />
      <div className="noise-overlay" />

      <div className="relative z-10 w-full max-w-md">
        <a
          href="/"
          className="font-syne font-bold text-2xl text-white tracking-tight block text-center mb-10"
        >
          BLANC<span className="text-brand-lime">SPACE</span>
        </a>

        <div className="glass-card rounded-3xl p-8 md:p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-brand-lime/20 flex items-center justify-center text-brand-lime">
              <Lock size={20} />
            </div>
            <div>
              <h1 className="font-syne font-bold text-2xl text-white">
                Admin Login
              </h1>
              <p className="font-mono text-xs text-zinc-500">
                // leads dashboard access
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={handleChange}
                data-testid="admin-login-email"
                className="form-input"
                placeholder="blancspace.agency@gmail.com"
              />
            </div>

            <div>
              <label className="block font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
                Password
              </label>
              <input
                type="password"
                name="password"
                required
                value={form.password}
                onChange={handleChange}
                data-testid="admin-login-password"
                className="form-input"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="cta-button w-full justify-center"
            >
              {loading ? "Signing in..." : "Sign In"}
              <ArrowUpRight size={20} />
            </button>
          </form>
        </div>

        <p className="text-center mt-6 font-mono text-xs text-zinc-600">
          <a href="/" className="hover:text-brand-lime transition-colors">
            ← back to site
          </a>
        </p>
      </div>
    </div>
  );
}
